import type { MuscleGroup } from "@training/shared";
import type { JournalEntry } from "./api.ts";
import { MUSCLE_LABEL } from "./muscles.ts";
import { format } from "./session.ts";

/** "850 kg" under a tonne, "1,8 t" from there: a tonne of 0,1 t said nothing. */
export function formatVolume(kg: number): string {
  return kg < 1000 ? `${Math.round(kg)} kg` : `${format(Math.round(kg / 100) / 10)} t`;
}

/** "34 séries · 1,8 t · effort 8/10": what a session held, on one line. */
export function journalFigures(entry: JournalEntry): string {
  if (entry.status === "in_progress") return entry.sets > 0 ? `En cours · ${plural(entry.sets, "série")}` : "En cours";
  const parts = [plural(entry.sets, "série")];
  if (entry.volumeKg !== null && entry.volumeKg > 0) parts.push(formatVolume(entry.volumeKg));
  else if (entry.source === "sheet") parts.push("feuille");
  if (entry.perceivedEffort !== null) parts.push(`effort ${entry.perceivedEffort}/10`);
  return parts.join(" · ");
}

/**
 * The muscles a session worked most, "Quadriceps 6 · Ischios 4". The abs
 * stay out: the circuit gives them sixteen sets every time, which would put
 * them first on every line and say nothing.
 */
export function topMuscles(entry: JournalEntry, count = 3): string {
  return entry.muscles
    .filter((item) => item.muscle !== "abs")
    .slice(0, count)
    .map((item) => `${MUSCLE_LABEL[item.muscle as MuscleGroup]} ${format(item.sets)}`)
    .join(" · ");
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count > 1 ? "s" : ""}`;
}

/** The Monday of the week a day falls in, as the cycle counts its weeks. */
export function weekStart(iso: string): string {
  const day = new Date(`${iso}T12:00:00Z`);
  const offset = (day.getUTCDay() + 6) % 7;
  day.setUTCDate(day.getUTCDate() - offset);
  return day.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const day = new Date(`${iso}T12:00:00Z`);
  day.setUTCDate(day.getUTCDate() + days);
  return day.toISOString().slice(0, 10);
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

export interface MuscleWeek {
  muscle: MuscleGroup;
  /** Sets this week, a secondary muscle counting half. */
  now: number;
  /** The same, the week before: what "now" is read against. */
  before: number;
}

/**
 * Sets per muscle this week against the week before, largest first. The
 * abs come apart: the circuit's sixteen sets a session would flatten every
 * other bar.
 */
export function weeklyMuscles(
  entries: JournalEntry[],
  today: string,
): { start: string; muscles: MuscleWeek[]; abs: MuscleWeek | null } {
  const start = weekStart(today);
  const previous = addDays(start, -7);
  const tally = new Map<MuscleGroup, MuscleWeek>();
  for (const entry of entries) {
    const week = weekStart(entry.date);
    if (week !== start && week !== previous) continue;
    for (const { muscle, sets } of entry.muscles) {
      const row = tally.get(muscle) ?? { muscle, now: 0, before: 0 };
      if (week === start) row.now += sets;
      else row.before += sets;
      tally.set(muscle, row);
    }
  }
  const rows = [...tally.values()].sort((a, b) => b.now - a.now || b.before - a.before);
  return { start, muscles: rows.filter((row) => row.muscle !== "abs"), abs: tally.get("abs") ?? null };
}

/**
 * Muscles trained this past month but left alone a week or more: what the
 * home screen points at. A muscle never trained in that month is not
 * forgotten, it is simply not in the programme.
 */
export function restingMuscles(entries: JournalEntry[], today: string): { muscle: MuscleGroup; days: number }[] {
  const last = new Map<MuscleGroup, string>();
  for (const entry of entries) {
    if (daysBetween(entry.date, today) > 28) continue;
    for (const { muscle } of entry.muscles) {
      if (muscle === "abs") continue;
      const seen = last.get(muscle);
      if (!seen || entry.date > seen) last.set(muscle, entry.date);
    }
  }
  return [...last]
    .map(([muscle, date]) => ({ muscle, days: daysBetween(date, today) }))
    .filter((item) => item.days >= 7)
    .sort((a, b) => b.days - a.days);
}

/**
 * Why a session is the one to do: of the muscles it works, the one left
 * alone the longest — "Dos : rien depuis 8 jours". Null when none of them
 * has been waiting a week: then it is simply the session done longest ago.
 */
export function suggestionReason(
  muscles: readonly MuscleGroup[],
  resting: readonly { muscle: MuscleGroup; days: number }[],
): string | null {
  const longest = resting.find((item) => muscles.includes(item.muscle));
  return longest ? `${MUSCLE_LABEL[longest.muscle]} : rien depuis ${longest.days} jours` : null;
}

/** "Quadriceps · Fessiers · Ischios": what a session works, at a glance. */
export function musclesLine(muscles: readonly MuscleGroup[], count = 3): string {
  return muscles
    .slice(0, count)
    .map((muscle) => MUSCLE_LABEL[muscle])
    .join(" · ");
}
